import {
    Text,
    type BlockNode,
    type Descendant,
    type TableCellElement,
    type TableElement,
    type TableRowElement,
} from "slate";

import SimpleMarkdown, {type SingleASTNode} from "@khanacademy/simple-markdown";
import {mathRule, textRule} from "@khan/component-markdown/rules";

import {
    blockQuote,
    bulletedList,
    codeBlock,
    codeInline,
    headingOne,
    headingThree,
    headingTwo,
    horizontalRule,
    image,
    letteredList,
    li,
    link,
    liText,
    mathBlock,
    mathInline,
    numberedList,
    paragraph,
    skeletonBlock,
    table,
    tableBody,
    tableCell,
    tableHeader,
    tableRow,
    text,
} from "./node-builders.ts";
import {INDENT_SPACES} from "./slate-to-markdown.ts";

const numberedListItemPrefixRegex = /^[0-9]+\.$/;
const letteredListItemPrefixRegex = /^[a-zA-Z]\.$/;

export const markdownInlineParser = SimpleMarkdown.parserFor(
    {
        // NOTE(reid): For each of these, we should have corresponding
        // logic below in markdownInlineToSlateNode to handle converting
        // the parsed Markdown AST node into a Slate node.
        text: textRule,
        inlineMath: mathRule,
        inlineCode: SimpleMarkdown.defaultRules.inlineCode,
        em: SimpleMarkdown.defaultRules.em,
        u: SimpleMarkdown.defaultRules.u,
        strong: SimpleMarkdown.defaultRules.strong,
        link: SimpleMarkdown.defaultRules.link,
        image: SimpleMarkdown.defaultRules.image,
    },
    {inline: true},
);

const markdownTableParser = SimpleMarkdown.parserFor(
    {
        tableSeparator: SimpleMarkdown.defaultRules.tableSeparator,
        table: SimpleMarkdown.defaultRules.table,
        text: textRule,
    },
    {inline: false},
);

/**
 * Handles converting a "chunk" of Markdown into a Slate block node. Because
 * the backend batches tokens into chunks, we can assume that each chunk given
 * here should be handled as a complete Markdown block (the exception is list
 * items, which are also streamed as individual chunks).
 *
 * That means this function can first look at the prefix of the chunk to see
 * what kind of block it should be (e.g. "# " means a heading-one), and then
 * can plug into the SimpleMarkdown parser to handle parsing inlines within
 * that block.
 *
 * A single list-item chunk will get returned as a full list node with that
 * single list-item in it. This allows us to handle a series of list-items as
 * if each one is an independent list. Slate's normalization pass will handle
 * merging two sibling lists of one item into a single list of two items.
 */
export const markdownChunkToSlateBlock = (chunk: string): BlockNode => {
    const [prefix, rest] = extractPrefix(chunk);

    switch (prefix) {
        case "#":
            return headingOne(...parseInlines(rest));
        case "##":
            return headingTwo(...parseInlines(rest));
        case "###":
        case "####":
        case "#####":
        case "######":
            // NOTE(reid): For now, we consider all of these h3s, but we could
            // someday support h4, h5, h6 as separate node types.
            return headingThree(...parseInlines(rest));
        case ">":
            const lines = rest
                .split("\n")
                .map((line) => line.replace(/^> +/, ""))
                .map(text);
            return blockQuote(...lines);
        case "*":
        case "-":
        case "+":
            return bulletedList(li(liText(...parseInlines(rest))));
    }

    if (numberedListItemPrefixRegex.test(prefix)) {
        return numberedList(li(liText(...parseInlines(rest))));
    }

    if (letteredListItemPrefixRegex.test(prefix)) {
        return letteredList(li(liText(...parseInlines(rest))));
    }

    if (chunk.startsWith("\\[\n") && chunk.endsWith("\n\\]")) {
        return mathBlock(chunk.substring(3, chunk.length - 3));
    }

    if (chunk.startsWith("```") && chunk.endsWith("\n```")) {
        const [language, ...codeLines] = chunk
            .substring(3, chunk.length - 4)
            .split("\n");
        return codeBlock(language, text(codeLines.join("\n")));
    }

    if (chunk.startsWith("| ") && chunk.endsWith(" |")) {
        const ast = markdownTableParser(chunk);
        if (ast && ast.length && ast[0].type === "table") {
            return buildTable(ast[0]);
        }
    }

    if (chunk.startsWith("[@placeholder ") && chunk.endsWith("]")) {
        return skeletonBlock(extractPlaceholderKey(chunk));
    }

    if (/-{3,}/.test(chunk)) {
        return horizontalRule();
    }

    return paragraph(...parseInlines(chunk));
};

export const extractIndentDepth = (chunk: string): number => {
    const match = chunk.match(/^(\s*)[^ ]/);
    if (match) {
        // We extract the indent (any whitespace at the start), the
        // prefix (a sequence of non-space characters after that), and
        // then the rest of the chunk after the prefix.

        // Assuming each indent level is represented by 4 spaces
        return Math.ceil(match[1].length / INDENT_SPACES);
    }

    return 0;
};

const parseInlines = (chunkText: string) => {
    const markdownNodes = markdownInlineParser(chunkText);
    const slateNodes = markdownNodes.flatMap(markdownInlineToSlateNode);

    if (!slateNodes.length) {
        // If, for some reason, the Markdown node didn't have any content,
        // we return the Slate default for an empty block so that Slate
        // doesn't error in strange places. Slate generally requires even
        // empty blocks to have at least one text node like this.
        return [text("")];
    }

    // Ensure that the last node in the list is a text node so that we can move
    // the cursor to the end of the block.
    const lastNode = slateNodes[slateNodes.length - 1];
    if (!Text.isText(lastNode)) {
        slateNodes.push(text(""));
    }

    return slateNodes;
};

const markdownInlineToSlateNode = (inlineNode: SingleASTNode): Descendant[] => {
    if (inlineNode.type === "strong") {
        const children = inlineNode.content as SingleASTNode[];
        const nodes = children.flatMap(markdownInlineToSlateNode);
        return nodes.map((n) => ({...n, bold: true}));
    }

    if (inlineNode.type === "em") {
        const children = inlineNode.content as SingleASTNode[];
        const nodes = children.flatMap(markdownInlineToSlateNode);
        return nodes.map((n) => ({...n, italic: true}));
    }

    if (inlineNode.type === "u") {
        const children = inlineNode.content as SingleASTNode[];
        const nodes = children.flatMap(markdownInlineToSlateNode);
        return nodes.map((n) => ({...n, underline: true}));
    }

    if (inlineNode.type === "link") {
        const children = inlineNode.content as SingleASTNode[];
        const nodes = children.flatMap(markdownInlineToSlateNode);
        return [link(inlineNode.target, ...nodes)];
    }

    if (inlineNode.type === "image") {
        return [image(inlineNode.target, inlineNode.alt)];
    }

    if (inlineNode.type === "inlineMath") {
        return [mathInline(inlineNode.content)];
    }

    if (inlineNode.type === "inlineCode") {
        return [codeInline(text(inlineNode.content))];
    }

    return [text(inlineNode.content || "")];
};

const extractPrefix = (chunk: string): [string, string] => {
    const match = chunk.match(/^\s*([^ ]+) +/);
    if (match) {
        // We extract the prefix (a sequence of non-space characters after
        // any initial spaces), then the rest of the chunk after the prefix.
        return [match[1], chunk.substring(match[0].length)];
    }

    return ["", chunk];
};

const buildTable = (tableMarkdownNode: SingleASTNode): TableElement => {
    const headerCells: TableCellElement[] = (
        tableMarkdownNode.header || []
    ).map((cell) => {
        const cellText = cell.map((part) => part.content || "").join("");

        return tableCell(...parseInlines(cellText));
    });

    const bodyRows: TableRowElement[] = tableMarkdownNode.cells.map((row) => {
        const cells: TableCellElement[] = row.map((cell) => {
            const cellText = cell
                .map((part) => part.content || "")
                .join("")
                .replaceAll(/<br[ ]*\/?>/g, "\n");

            return tableCell(...parseInlines(cellText));
        });

        return tableRow(...cells);
    });

    if (!headerCells.length) {
        return table(tableBody(...bodyRows));
    }

    return table(tableHeader(tableRow(...headerCells)), tableBody(...bodyRows));
};

const extractPlaceholderKey = (chunk: string): string => {
    const match = chunk.match(/\[@placeholder (.+)\]/);
    if (match) {
        return match[1];
    }

    return "";
};

export function markdownStringToSlateElements(markdown: string): Element[] {
    return markdown
        .split("\n")
        .map((line) => markdownChunkToSlateBlock(line)) as unknown as Element[];
}
